import { Injectable, inject } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { HttpError, HttpResponseAlt } from '@shared/utils/models/http.model';
import { switchMap, catchError, of, map, retry, filter, mergeMap } from 'rxjs';
import { AffiliateService } from '../services/affiliate.service';
import { Action, ActionCreator, Creator, Store } from '@ngrx/store';
import { AffiliateActions } from '../actions/affiliate.actions';
import { UtilityActions } from '../actions/utility.actions';
import { AffiliateProfile } from '@core/utils/models/affiliate.model';
import { AffiliateActionName } from '@core/utils/models/utility.model';
import { RegisterActions } from '@features/auth/data-access/actions/auth.actions';
import { concatLatestFrom } from '@ngrx/operators';
import { selectAffiliateHash } from '../selectors/affiliate.selectors';
import { selectAffiliateActionsState } from '../selectors/utility.selectors';
import { selectRouterState } from '../selectors/route.selectors';

const AFFILIATE_ACTION_MAP: {
  name: AffiliateActionName;
  action: ActionCreator<string, Creator>;
}[] = [
  { name: AffiliateActionName.VISIT, action: UtilityActions.getAffiliateActionsSuccess },
  { name: AffiliateActionName.SIGNUP, action: RegisterActions.registerSuccess },
];

@Injectable()
export class AffiliateEffects {
  private readonly affiliateService = inject(AffiliateService);
  private readonly actions$ = inject(Actions);
  private readonly store = inject(Store);

  saveHash$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(AffiliateActions.saveHash),
      switchMap((action) => {
        this.affiliateService.saveAffiliateHashToCookie(action.hash);
        return [AffiliateActions.saveHashSuccess(), AffiliateActions.setHash({ hash: action.hash })];
      }),
      catchError((error: HttpError) => of(AffiliateActions.saveHashFailure({ error })))
    );
  });

  setHash$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(AffiliateActions.setHash),
      switchMap((action) => [
        AffiliateActions.getProfile({ hash: action.hash }),
        UtilityActions.getAffiliateActions(),
        UtilityActions.getAffiliateTypes(),
      ])
    );
  });

  getProfile$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(AffiliateActions.getProfile),
      switchMap((action) =>
        this.affiliateService.getAffiliateProfile(action.hash).pipe(
          map((response: HttpResponseAlt<[AffiliateProfile]>) =>
            AffiliateActions.getProfileSuccess({ data: response.data[0] })
          ),
          retry({ count: 5, delay: 5000 }),
          catchError((error: HttpError) => of(AffiliateActions.getProfileFailure({ error })))
        )
      )
    );
  });

  recordAction$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(...AFFILIATE_ACTION_MAP.map(({ action }) => action)),
      concatLatestFrom(() => [
        this.store.select(selectAffiliateHash),
        this.store.select(selectAffiliateActionsState),
        this.store.select(selectRouterState),
      ]),
      filter(
        ([action, hash, { data }, route]) =>
          !!hash &&
          !!data &&
          !(route.state.url !== '/' && (action as Action).type === UtilityActions.getAffiliateActionsSuccess.type)
      ),
      mergeMap(([action, hash, { data }]) => {
        const actionName = AFFILIATE_ACTION_MAP.find((item) => item.action.type === (action as any).type)?.name;
        const actionId = data?.find(({ action_en }) => action_en === actionName)?.id;

        if (!actionName || !actionId || !hash)
          return of(AffiliateActions.recordActionFailure({ error: { message: `Action not found`, code: null } }));

        return this.affiliateService.recordAffiliateAction(hash, actionId).pipe(
          map(() => AffiliateActions.recordActionSuccess()),
          retry({ count: 5, delay: 5000 }),
          catchError((error: HttpError) => of(AffiliateActions.recordActionFailure({ error })))
        );
      })
    );
  });
}
